import React, { useState, useEffect } from 'react';
import EditInvoiceModal from './EditInvoiceModal.tsx';
import { AppIcon, invoiceTypeIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
}

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  yearsActive: string;
}

interface Payment {
  id: string;
  amount: string;
  status: string;
  paymentDate: string;
  paymentMethod: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  title: string;
  description: string;
  amount: string;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  type: 'membership' | 'lesson' | 'event' | 'equipment' | 'other';
  isRecurring: boolean;
  recurringFrequency?: 'monthly' | 'quarterly' | 'annually';
  issueDate: string;
  dueDate: string;
  paidAt?: string;
  paidAmount: string;
  createdAt: string;
  family: Family | null;
  creator: {
    firstName: string;
    lastName: string;
  };
  payments: Payment[];
  isNonMember?: boolean;
  nonMemberName?: string;
  nonMemberEmail?: string;
  nonMemberPhone?: string;
  nonMemberCompany?: string;
  paymentToken?: string;
}

interface CreateInvoiceModalProps {
  onClose: () => void;
  onSuccess: () => void;
  families: Family[];
  alumni: AlumniMember[];
}

function CreateInvoiceModal({ onClose, onSuccess, families, alumni }: CreateInvoiceModalProps) {
  const [formData, setFormData] = useState({
    recipientType: 'family' as 'family' | 'alumni' | 'non-member',
    familyId: '',
    alumniId: '',
    title: '',
    description: '',
    amount: '',
    type: 'other',
    dueDate: '',
    isRecurring: false,
    recurringFrequency: 'monthly',
    membershipLength: 12,
    isNonMember: false,
    nonMemberName: '',
    nonMemberEmail: '',
    nonMemberPhone: '',
    nonMemberCompany: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      
      const requestBody = formData.recipientType === 'non-member' ? {
        // Non-member invoice - don't send familyId or alumniId
        isNonMember: true,
        nonMemberName: formData.nonMemberName,
        nonMemberEmail: formData.nonMemberEmail,
        nonMemberPhone: formData.nonMemberPhone || undefined,
        nonMemberCompany: formData.nonMemberCompany || undefined,
        title: formData.title,
        description: formData.description,
        amount: parseFloat(formData.amount),
        type: formData.type,
        dueDate: formData.dueDate
      } : formData.recipientType === 'alumni' ? {
        // Alumni invoice
        alumniId: formData.alumniId,
        title: formData.title,
        description: formData.description,
        amount: parseFloat(formData.amount),
        type: formData.type,
        dueDate: formData.dueDate,
        isRecurring: false // Alumni don't get recurring invoices
      } : {
        // Family invoice - include only relevant fields
        familyId: formData.familyId,
        title: formData.title,
        description: formData.description,
        amount: parseFloat(formData.amount),
        type: formData.type,
        dueDate: formData.dueDate,
        isRecurring: formData.isRecurring,
        ...(formData.isRecurring && formData.recurringFrequency && {
          recurringFrequency: formData.recurringFrequency
        }),
        ...(formData.membershipLength && { membershipLength: parseInt(formData.membershipLength.toString()) })
      };
      
      console.log('Submitting invoice with data:', requestBody);
      
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        // Log full error details for debugging
        console.error('Invoice creation failed:', data);
        
        // Extract error messages
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map((err: any) => {
            // Include field name if available
            if (err.param && err.msg) {
              return `${err.param}: ${err.msg}`;
            }
            return err.msg || err.message || 'Unknown error';
          });
          setErrors(errorMessages);
        } else {
          setErrors([data.message || 'Failed to create invoice']);
        }
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setErrors(['Failed to create invoice. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Create New Invoice</h3>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recipient Type Selector */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.recipientType}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    recipientType: e.target.value as 'family' | 'alumni' | 'non-member',
                    familyId: '',
                    alumniId: '',
                    isRecurring: e.target.value === 'family' ? prev.isRecurring : false
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="family">Family Member</option>
                  <option value="alumni">Alumni Member</option>
                  <option value="non-member">Non-Member (External)</option>
                </select>
              </div>

              {formData.recipientType === 'family' ? (
                // Family Invoice - Family Selection
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Family <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.familyId}
                    onChange={(e) => setFormData(prev => ({ ...prev, familyId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  >
                    <option value="">Select a family</option>
                    {families.map(family => (
                      <option key={family.id} value={family.id}>
                        {family.familyName} ({family.primaryContactFirstName} {family.primaryContactLastName})
                      </option>
                    ))}
                  </select>
                </div>
              ) : formData.recipientType === 'alumni' ? (
                // Alumni Invoice - Alumni Selection
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alumni Member <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.alumniId}
                    onChange={(e) => setFormData(prev => ({ ...prev, alumniId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  >
                    <option value="">Select an alumni member</option>
                    {alumni.map(alumniMember => (
                      <option key={alumniMember.id} value={alumniMember.id}>
                        {alumniMember.firstName} {alumniMember.lastName} {alumniMember.yearsActive && `(${alumniMember.yearsActive})`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                // Non-Member Invoice - Contact Information
                <>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nonMemberName}
                      onChange={(e) => setFormData(prev => ({ ...prev, nonMemberName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="John Doe or ABC Company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.nonMemberEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, nonMemberEmail: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.nonMemberPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, nonMemberPhone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={formData.nonMemberCompany}
                      onChange={(e) => setFormData(prev => ({ ...prev, nonMemberCompany: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="ABC Corporation (optional)"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="e.g., Monthly Membership Fee - November 2024"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="Optional description of the invoice..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    type: e.target.value,
                    // Clear recurring options if switching away from membership
                    isRecurring: e.target.value === 'membership' ? prev.isRecurring : false
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="membership">Membership</option>
                  <option value="lesson">Lesson</option>
                  <option value="event">Event</option>
                  <option value="equipment">Equipment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              {formData.type === 'membership' && formData.recipientType === 'family' && (
                <div>
                  <label className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                      className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Recurring Membership (Family Only)</span>
                  </label>
                </div>
              )}

              {formData.isRecurring && formData.type === 'membership' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Billing Frequency</label>
                    <select
                      value={formData.recurringFrequency}
                      onChange={(e) => setFormData(prev => ({ ...prev, recurringFrequency: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Membership Length (months) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      required
                      value={formData.membershipLength || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, membershipLength: parseInt(e.target.value) || 12 }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="12"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will create {Math.ceil((formData.membershipLength || 12) / (formData.recurringFrequency === 'monthly' ? 1 : formData.recurringFrequency === 'quarterly' ? 3 : 12))} invoices for the membership period
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  loading || 
                  !formData.title || 
                  !formData.amount || 
                  !formData.dueDate ||
                  (formData.recipientType === 'family' && !formData.familyId) ||
                  (formData.recipientType === 'alumni' && !formData.alumniId) ||
                  (formData.recipientType === 'non-member' && (!formData.nonMemberName || !formData.nonMemberEmail))
                }
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function InvoiceManagement() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [alumni, setAlumni] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [monthFilter, setMonthFilter] = useState<string>('all');
  const [familyFilter, setFamilyFilter] = useState<string>('all');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchFamilies();
    fetchAlumni();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/invoices', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAlumni(data.data);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
    }
  };



  const handleEditInvoice = async (invoiceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setEditingInvoice(data.data);
      } else {
        alert('Failed to load invoice details: ' + data.message);
      }
    } catch (error) {
      console.error('Error loading invoice for edit:', error);
      alert('Failed to load invoice details.');
    }
  };

  const handleMarkAsPaid = async (invoice: Invoice) => {
    const paymentMethod = window.prompt(
      'Select payment method:\n1. Cash\n2. Check\n3. Bank Transfer\n4. Other\n\nEnter number (1-4):',
      '1'
    );

    if (!paymentMethod || !['1', '2', '3', '4'].includes(paymentMethod)) {
      return;
    }

    const methodMap: { [key: string]: string } = {
      '1': 'cash',
      '2': 'check',
      '3': 'bank_transfer',
      '4': 'other'
    };

    const paymentNotes = window.prompt(
      'Payment notes (optional):\nE.g., Check number, reference, etc.',
      ''
    );

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'paid',
          paymentMethod: methodMap[paymentMethod],
          paymentNotes: paymentNotes || `Manual payment - marked as paid by admin`
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('✅ Invoice marked as paid successfully!');
        fetchInvoices(); // Refresh the list
      } else {
        alert('Failed to mark invoice as paid: ' + data.message);
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      alert('Failed to mark invoice as paid.');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string, invoice?: any) => {
    // Check if this is a paid invoice
    const isPaidInvoice = invoice?.status === 'paid';
    
    // Check if this is a membership invoice with recurring group
    const isMembershipSeries = invoice?.type === 'membership' && invoice?.isRecurring;
    
    let deleteRelated = false;
    let allowPaidDeletion = false;
    let confirmMessage = 'Are you sure you want to delete this invoice? This action cannot be undone.';
    
    // Handle paid invoice confirmation
    if (isPaidInvoice) {
      const paidConfirm = window.confirm(
        '⚠️ WARNING: This is a PAID invoice!\n\n' +
        'Deleting paid invoices will permanently remove financial records and may affect accounting.\n\n' +
        'Are you sure you want to delete this paid invoice?'
      );
      
      if (!paidConfirm) {
        return;
      }
      
      allowPaidDeletion = true;
      confirmMessage = '⚠️ FINAL CONFIRMATION: Delete paid invoice?\n\nThis will permanently remove this paid invoice from the system.';
    }
    
    // Handle membership series confirmation
    if (isMembershipSeries) {
      const userChoice = window.confirm(
        'This is part of a membership series. Would you like to delete ALL related membership invoices?\n\n' +
        'Click OK to delete the entire membership series, or Cancel to delete only this invoice.'
      );
      deleteRelated = userChoice;
      
      if (deleteRelated) {
        if (isPaidInvoice) {
          confirmMessage = '⚠️ FINAL CONFIRMATION: Delete ENTIRE membership series including PAID invoices?\n\nThis will permanently remove all invoices in this membership series.';
        } else {
          confirmMessage = 'Are you sure you want to delete the ENTIRE membership series? This action cannot be undone.';
        }
      }
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deleteRelated: deleteRelated,
          allowPaidDeletion: allowPaidDeletion
        })
      });
      const data = await response.json();
      if (data.success) {
        const successMessage = data.deletedCount > 1 
          ? `Successfully deleted ${data.deletedCount} membership invoices!`
          : 'Invoice deleted successfully!';
        alert(successMessage);
        fetchInvoices(); // Refresh the list
      } else {
        if (data.requiresPaidConfirmation) {
          alert('This operation requires confirmation to delete paid invoices.');
        } else {
          alert('Failed to delete invoice: ' + data.message);
        }
        if (data.paidInvoices) {
          alert('The following invoices have been paid: ' + data.paidInvoices.join(', '));
        }
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice.');
    }
  };

  // Bulk selection handlers
  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    const newSelected = new Set(selectedInvoices);
    if (checked) {
      newSelected.add(invoiceId);
    } else {
      newSelected.delete(invoiceId);
    }
    setSelectedInvoices(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredInvoices.map(inv => inv.id);
      setSelectedInvoices(new Set(allIds));
    } else {
      setSelectedInvoices(new Set());
    }
  };

  // Bulk send functionality
  const handleBulkSend = async () => {
    if (selectedInvoices.size === 0) {
      alert('Please select invoices to send.');
      return;
    }

    const selectedInvoicesList = Array.from(selectedInvoices);
    const draftInvoices = selectedInvoicesList.filter(id => {
      const invoice = invoices.find(inv => inv.id === id);
      return invoice?.status === 'draft';
    });

    const nonDraftInvoices = selectedInvoices.size - draftInvoices.length;

    if (draftInvoices.length === 0) {
      alert('None of the selected invoices are in draft status. Only draft invoices can be sent.');
      return;
    }

    let confirmMessage = `Send ${draftInvoices.length} draft invoice(s)?`;
    
    if (nonDraftInvoices > 0) {
      confirmMessage = `${draftInvoices.length} draft invoice(s) will be sent.\n${nonDraftInvoices} invoice(s) will be skipped (already sent/paid).\n\nContinue?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSending(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const token = localStorage.getItem('authToken');
      
      // Send invoices one by one
      for (const invoiceId of draftInvoices) {
        try {
          const response = await fetch(`/api/invoices/${invoiceId}/send`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        alert(`Successfully sent ${successCount} invoice(s).` + 
              (errorCount > 0 ? ` ${errorCount} failed to send.` : ''));
        fetchInvoices(); // Refresh the list
        setSelectedInvoices(new Set()); // Clear selection
      } else {
        alert('Failed to send invoices. Please try again.');
      }

    } catch (error) {
      console.error('Error in bulk send:', error);
      alert('An error occurred while sending invoices. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Bulk delete functionality
  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) {
      alert('Please select invoices to delete.');
      return;
    }

    const selectedInvoicesList = Array.from(selectedInvoices);
    const paidInvoices = selectedInvoicesList.filter(id => {
      const invoice = invoices.find(inv => inv.id === id);
      return invoice?.status === 'paid';
    });

    let allowPaidDeletion = false;
    let confirmMessage = `Are you sure you want to delete ${selectedInvoices.size} selected invoice(s)? This action cannot be undone.`;

    // Handle paid invoices in bulk selection
    if (paidInvoices.length > 0) {
      const paidConfirm = window.confirm(
        `⚠️ WARNING: ${paidInvoices.length} of the selected invoices are PAID!\n\n` +
        'Deleting paid invoices will permanently remove financial records and may affect accounting.\n\n' +
        'Do you want to continue and delete the paid invoices as well?'
      );
      
      if (!paidConfirm) {
        return;
      }
      
      allowPaidDeletion = true;
      confirmMessage = `⚠️ FINAL CONFIRMATION: Delete ${selectedInvoices.size} invoice(s) including ${paidInvoices.length} PAID invoice(s)?\n\nThis will permanently remove these invoices from the system.`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      const token = localStorage.getItem('authToken');
      
      // Delete invoices one by one (could be optimized with a bulk endpoint)
      for (const invoiceId of selectedInvoicesList) {
        try {
          const response = await fetch(`/api/invoices/${invoiceId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              deleteRelated: false, // Individual deletion for bulk
              allowPaidDeletion: allowPaidDeletion 
            })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        alert(`Successfully deleted ${successCount} invoice(s).` + 
              (errorCount > 0 ? ` ${errorCount} failed to delete.` : ''));
        fetchInvoices(); // Refresh the list
        setSelectedInvoices(new Set()); // Clear selection
      } else {
        alert('Failed to delete invoices. Please try again.');
      }

    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('Error occurred during bulk deletion.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Generate unique months from invoice due dates
  const getAvailableMonths = () => {
    const months = new Set<string>();
    invoices.forEach(invoice => {
      const dueDate = new Date(invoice.dueDate);
      const monthKey = dueDate.getFullYear() + '-' + String(dueDate.getMonth() + 1).padStart(2, '0');
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse(); // Most recent first
  };

  const formatMonthDisplay = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const filteredInvoices = invoices.filter(invoice => {
    // Status filter
    if (filter !== 'all' && invoice.status !== filter) return false;
    
    // Month filter (based on due date)
    if (monthFilter !== 'all') {
      const dueDate = new Date(invoice.dueDate);
      const filterMonth = dueDate.getFullYear() + '-' + String(dueDate.getMonth() + 1).padStart(2, '0');
      if (filterMonth !== monthFilter) return false;
    }
    
    // Family filter
    if (familyFilter !== 'all' && invoice.family?.id !== familyFilter) return false;
    
    return true;
  });

  const stats = {
    total: invoices.length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalAmount: invoices.reduce((sum, invoice) => sum + parseFloat(invoice.amount), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((sum, invoice) => sum + parseFloat(invoice.paidAmount), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
        <span className="ml-2 text-gray-600">Loading invoices...</span>
      </div>
    );
  }

  return (
    <div className="px-12 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoice Management</h1>
          <p className="text-gray-600 mt-2">Create and manage invoices for GGAA families</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-ggaa-green text-white px-6 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium"
        >
          Create New Invoice
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Invoices</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{stats.sent}</div>
          <div className="text-sm text-gray-600">Sent</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
          <div className="text-sm text-gray-600">Paid</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
          <div className="text-sm text-gray-600">Overdue</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-ggaa-green">${stats.paidAmount.toFixed(2)}</div>
          <div className="text-sm text-gray-600">Collected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        
        {/* Status Filter */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="flex space-x-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-ggaa-green text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total})
            </button>
            {['sent', 'paid', 'overdue', 'draft'].map(status => {
              const count = invoices.filter(i => i.status === status).length;
              return (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === status 
                      ? 'bg-ggaa-green text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Month and Family Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Month Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date Month</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
            >
              <option value="all">All Months</option>
              {getAvailableMonths().map(month => (
                <option key={month} value={month}>
                  {formatMonthDisplay(month)}
                </option>
              ))}
            </select>
          </div>

          {/* Family Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Family</label>
            <select
              value={familyFilter}
              onChange={(e) => setFamilyFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
            >
              <option value="all">All Families</option>
              {families.map(family => (
                <option key={family.id} value={family.id}>
                  {family.primaryContactFirstName} {family.primaryContactLastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(filter !== 'all' || monthFilter !== 'all' || familyFilter !== 'all') && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setFilter('all');
                setMonthFilter('all');
                setFamilyFilter('all');
              }}
              className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {filteredInvoices.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedInvoices.size > 0 && selectedInvoices.size === filteredInvoices.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Select All</span>
              </label>
              {selectedInvoices.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedInvoices.size} selected
                </span>
              )}
            </div>
            
            {selectedInvoices.size > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkSend}
                  disabled={isSending}
                  className="px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  {isSending ? 'Sending...' : `Send Selected (${selectedInvoices.size})`}
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  {isDeleting ? 'Deleting...' : `Delete Selected (${selectedInvoices.size})`}
                </button>
                <button
                  onClick={() => setSelectedInvoices(new Set())}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                >
                  Clear Selection
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <div className="py-6 bg-white rounded-xl shadow-lg">
          <EmptyState
            icon="emptyMoney"
            title="No invoices found"
            description={
              filter === 'all'
                ? "You haven't created any invoices yet."
                : `No ${filter} invoices found.`
            }
          />
          <div className="text-center pb-6">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-ggaa-green text-white px-6 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors"
          >
            Create Your First Invoice
          </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Family</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.has(invoice.id)}
                        onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                        className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <AppIcon name={invoiceTypeIcon(invoice.type)} className="w-5 h-5 mr-2 shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                          <div className="text-sm text-gray-500">{invoice.title}</div>
                          {invoice.isRecurring && (
                            <div className="text-xs text-blue-600">🔄 {invoice.recurringFrequency}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.family ? (
                        <>
                          <div className="text-sm font-medium text-gray-900">{invoice.family.familyName}</div>
                          <div className="text-sm text-gray-500">{invoice.family.primaryContactFirstName} {invoice.family.primaryContactLastName}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm font-medium text-gray-900">🏢 {invoice.nonMemberName || 'Non-Member'}</div>
                          <div className="text-sm text-gray-500">{invoice.nonMemberEmail}</div>
                          {invoice.nonMemberCompany && (
                            <div className="text-xs text-gray-400">{invoice.nonMemberCompany}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatCurrency(invoice.amount)}</div>
                      {parseFloat(invoice.paidAmount) > 0 && (
                        <div className="text-sm text-green-600">Paid: {formatCurrency(invoice.paidAmount)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(invoice.dueDate)}
                      {invoice.status === 'overdue' && (
                        <div className="text-xs text-red-600">Overdue</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEditInvoice(invoice.id)}
                          className="text-green-600 hover:text-green-900 font-medium"
                        >
                          Edit
                        </button>
                        {invoice.status !== 'paid' && (
                          <button 
                            onClick={() => handleMarkAsPaid(invoice)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Mark Paid
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteInvoice(invoice.id, invoice)}
                          className={`font-medium ${
                            invoice.status === 'paid' 
                              ? 'text-red-700 hover:text-red-900 font-bold' 
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={invoice.status === 'paid' ? 'Delete paid invoice (requires confirmation)' : 'Delete invoice'}
                        >
                          {invoice.status === 'paid' ? 'Delete (paid)' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          families={families}
          alumni={alumni}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchInvoices();
          }}
        />
      )}

      {/* Edit Invoice Modal */}
      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          families={families}
          onClose={() => setEditingInvoice(null)}
          onSuccess={() => {
            setEditingInvoice(null);
            fetchInvoices();
          }}
        />
      )}
    </div>
  );
} 
